/**
 * One small icon+label button in a reservation row's action group (BON-472).
 *
 * `disabledReason` carries both facts at once, so a caller cannot disable a
 * button without saying why: the string refuses the action AND becomes the
 * tooltip. A refused control with no explanation is the same dead end as a live
 * control that 409s.
 *
 * `aria-disabled` RATHER THAN `disabled`, which is what makes the reason
 * reachable at all. A `disabled` button cannot take focus and browsers suppress
 * its pointer events, so both routes to the reason — hover and Tab — are closed
 * on the one control that has something to explain. Every one of these tooltips
 * carried its reason in a `title` attribute and not one of them could ever
 * appear; the operator hit it on first contact.
 *
 * The trade is that the browser no longer refuses the click for us, so this file
 * has to: `activate` is the single door, and it is shut while a reason exists.
 */
import { useId } from 'react';

import { Tooltip } from '../../shared/Tooltip';

export function ReservationActionButton({
  icon,
  label,
  tone = 'primary',
  disabledReason,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  /** `danger` is the destructive end of the group — Release. */
  tone?: 'primary' | 'danger';
  /** Why this cannot be done right now, or `null` when it can. */
  disabledReason: string | null;
  onClick: () => void;
}) {
  const refused = disabledReason !== null;
  // Per instance: the same label recurs on every row of the table, so a label-
  // derived id would have several rows' buttons pointing at one description.
  const panelId = useId();
  // Inside the handler rather than by leaving `onClick` off, so a keyboard
  // activation, a synthetic click and an assistive-technology one all meet the
  // same refusal — there is no longer a `disabled` attribute stopping any of
  // them.
  const activate = () => {
    if (!refused) onClick();
  };

  const button = (
    <button
      type="button"
      onClick={activate}
      aria-disabled={refused || undefined}
      aria-describedby={refused ? panelId : undefined}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-stone-300 px-2.5 py-1.5 text-xs font-semibold dark:border-stone-600 ${refused ? REFUSED_CLASS : TONE_CLASS[tone]}`}
    >
      {icon}
      {label}
    </button>
  );

  if (!refused) return button;
  // The panel's id is handed to the button rather than left on the wrapper: a
  // description on a non-focusable span is announced to nobody.
  return (
    <Tooltip content={disabledReason} id={panelId}>
      {button}
    </Tooltip>
  );
}

const TONE_CLASS = {
  primary: 'text-moss-700 hover:bg-moss-50 dark:text-moss-300 dark:hover:bg-moss-900/20',
  danger: 'text-clay-700 hover:bg-clay-50 dark:text-clay-300 dark:hover:bg-clay-900/20',
};

/** No hover affordance and no tone: a refused button must not look like it is
 *  about to do the thing it will not do. */
const REFUSED_CLASS = 'cursor-not-allowed text-stone-500 opacity-40 dark:text-stone-400';
